export default function WhyChooseSection() {
  const steps = [
    {
      num: '01',
      icon: 'ri-phone-line',
      title: 'Vous nous appelez',
      desc: 'Un conseiller expert vous répond immédiatement et évalue votre situation par téléphone, gratuitement.'
    },
    {
      num: '02',
      icon: 'ri-home-4-line',
      title: 'Étude personnalisée',
      desc: 'Nous analysons votre logement, vos besoins et calculons vos économies potentielles en 48h.'
    },
    {
      num: '03',
      icon: 'ri-file-list-3-line',
      title: 'Devis & aides',
      desc: 'Vous recevez un devis transparent avec toutes les aides de l\'État déduites. Pas de surprise.'
    },
    {
      num: '04',
      icon: 'ri-tools-line',
      title: 'Installation pro',
      desc: 'Nos techniciens certifiés RGE interviennent chez vous. Installation soignée, rapide et propre.'
    }
  ];

  const reviews = [
    {
      name: 'Marie-Claire D.',
      location: 'Île-de-France',
      text: 'Équipe sérieuse et professionnelle. Ma facture de chauffage a baissé de 60% dès le premier hiver. Je recommande vivement !',
      rating: 5,
      product: 'Pompe à chaleur air/eau',
      avatar: "https://public.readdy.ai/ai/img_res/16b23a204bb79ee927bab06d17ed9f0d.jpg"
    },
    {
      name: 'Jean-Paul M.',
      location: 'Rhône-Alpes',
      text: 'Installation des panneaux en une seule journée. Le suivi est impeccable, ils m\'ont aidé pour toutes les démarches MaPrimeRénov\'.',
      rating: 5,
      product: 'Panneaux photovoltaïques',
      avatar: "https://readdy.ai/api/search-image?query=Professional%20headshot%20portrait%20of%20a%20smiling%20French%20man%20in%20his%2040s%2C%20friendly%20expression%2C%20natural%20light%2C%20neutral%20background%2C%20high%20quality%20portrait%20photography&width=80&height=80&seq=avatar-2&orientation=squarish"
    },
    {
      name: 'Sophie L.',
      location: 'Normandie',
      text: 'Super expérience du début à la fin. Le devis était clair, l\'installation rapide et maintenant je produis ma propre électricité !',
      rating: 5,
      product: 'Système solaire combiné',
      avatar: "https://readdy.ai/api/search-image?query=Professional%20headshot%20portrait%20of%20a%20young%20French%20woman%20smiling%2C%20warm%20approachable%20expression%2C%20bright%20natural%20light%2C%20clean%20neutral%20background%2C%20lifestyle%20photography&width=80&height=80&seq=avatar-3&orientation=squarish"
    }
  ];

  return (
    <section id="pourquoi" className="py-24 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">

        {/* Header */}
        <div className="text-center mb-16">
          <span className="inline-block bg-emerald-100 text-emerald-600 text-sm font-bold px-4 py-1.5 rounded-full mb-4">Pourquoi AIRPURE</span>
          <h2 className="text-4xl lg:text-5xl font-extrabold text-slate-900 mb-4">
            Simple, rapide<br />
            <span className="text-emerald-500">et sans mauvaise surprise</span>
          </h2>
          <p className="text-lg text-slate-500 max-w-2xl mx-auto">
            De l'appel jusqu'à la mise en service, on s'occupe de tout. Vous n'avez qu'à profiter des économies.
          </p>
        </div>

        {/* Steps */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-20">
          {steps.map((s, i) => (
            <div key={i} className="relative bg-white rounded-2xl p-7 group hover:shadow-md transition-shadow duration-200">
              <div className="text-5xl font-black text-slate-100 absolute top-5 right-6 leading-none select-none">{s.num}</div>
              <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center mb-5">
                <i className={`${s.icon} text-emerald-600 text-xl`}></i>
              </div>
              <h3 className="font-extrabold text-slate-800 text-lg mb-2">{s.title}</h3>
              <p className="text-slate-500 text-sm leading-relaxed">{s.desc}</p>
              {i < steps.length - 1 && (
                <div className="hidden lg:block absolute -right-3 top-1/2 -translate-y-1/2 z-10">
                  <i className="ri-arrow-right-s-line text-2xl text-slate-300"></i>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Trust belt */}
        <div className="bg-white rounded-3xl p-8 mb-20">
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-6 items-center">
            {[
              { icon: 'ri-shield-check-line', label: 'Certifié RGE', sub: 'Reconnu Garant Environnement', color: 'text-emerald-500 bg-emerald-50' },
              { icon: 'ri-government-line', label: 'Partenaire officiel', sub: 'MaPrimeRénov\'', color: 'text-orange-500 bg-orange-50' },
              { icon: 'ri-award-line', label: 'Qualibat certifié', sub: 'Artisan de confiance', color: 'text-amber-500 bg-amber-50' },
              { icon: 'ri-leaf-line', label: 'Éco-responsable', sub: 'Solutions durables', color: 'text-teal-500 bg-teal-50' },
              { icon: 'ri-customer-service-2-line', label: 'SAV 7j/7', sub: 'Intervention sous 48h', color: 'text-rose-500 bg-rose-50' },
            ].map((item, i) => (
              <div key={i} className="text-center">
                <div className={`w-14 h-14 ${item.color} rounded-2xl flex items-center justify-center mx-auto mb-3`}>
                  <i className={`${item.icon} text-2xl`}></i>
                </div>
                <p className="font-bold text-slate-800 text-sm">{item.label}</p>
                <p className="text-xs text-slate-400">{item.sub}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Reviews */}
        <div className="text-center mb-10">
          <h3 className="text-2xl font-extrabold text-slate-900 mb-2">Ce que disent nos clients</h3>
          <div className="flex items-center justify-center gap-1 mb-1">
            {[1,2,3,4,5].map(i => <i key={i} className="ri-star-fill text-amber-400 text-xl"></i>)}
          </div>
          <p className="text-slate-500 text-sm">4.9/5 basé sur +2 000 avis vérifiés</p>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {reviews.map((r, i) => (
            <div key={i} className="bg-white rounded-2xl p-7">
              <div className="flex items-center gap-1 mb-4">
                {[1,2,3,4,5].map(s => <i key={s} className="ri-star-fill text-amber-400 text-sm"></i>)}
              </div>
              <p className="text-slate-700 leading-relaxed mb-5 italic">&ldquo;{r.text}&rdquo;</p>
              <div className="flex items-center gap-3">
                <img src={r.avatar} alt={r.name} className="w-11 h-11 rounded-full object-cover" />
                <div>
                  <p className="font-bold text-slate-800 text-sm">{r.name}</p>
                  <p className="text-xs text-slate-400">{r.location} — {r.product}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* CTA Banner */}
        <div className="mt-14 bg-gradient-to-r from-slate-900 to-slate-800 rounded-3xl p-10 flex flex-col lg:flex-row items-center justify-between gap-8">
          <div>
            <h3 className="text-2xl lg:text-3xl font-extrabold text-white mb-2">Prêt à réduire votre facture ?</h3>
            <p className="text-slate-300">Nos experts vous rappellent gratuitement dans les 2 heures.</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <a href="#contact" className="bg-orange-500 hover:bg-orange-600 text-white font-bold px-8 py-4 rounded-full transition-colors cursor-pointer whitespace-nowrap text-center">
              Devis gratuit en 2 min
            </a>
            <a href="tel:0189293323" className="border-2 border-white/30 hover:border-white text-white font-semibold px-8 py-4 rounded-full transition-colors cursor-pointer whitespace-nowrap text-center">
              <i className="ri-phone-line mr-2"></i>01 89 29 33 23
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
